import { Injectable, signal } from '@angular/core';

export interface SecretToShow {
  /** Already translated. What the secret belongs to, so the dialog says which one. */
  readonly titleKey: string;
  readonly name: string;
  readonly secret: string;
}

/**
 * A secret that can be read exactly once.
 *
 * **The one place a blocking dialog is right**, and for a reason that has nothing to do with
 * emphasis: the value is not stored anywhere it can be read again, so the interface has to stop and
 * make sure it was taken. A route would be worse still -- the secret would end up in the address bar,
 * in the history, and in whatever logs the browser keeps.
 *
 * Deliberately shaped like `ConfirmService`: the caller hands over a value and forgets about it, and
 * `SecretDialog` renders whatever is pending. Nothing here keeps the secret after the dialog closes.
 */
@Injectable({ providedIn: 'root' })
export class SecretService {
  private readonly _pending = signal<SecretToShow | null>(null);

  readonly pending = this._pending.asReadonly();

  show(secret: SecretToShow): void {
    this._pending.set(secret);
  }

  /** Called when the dialog closes. Dropping the value is the point, not housekeeping. */
  dismiss(): void {
    this._pending.set(null);
  }
}
